import { groupBy, partition } from 'lodash/fp';

import { GoogleSupportedLanguage } from 'dashboard/lib/constants/locales';
import {
  BoundingBox,
  DefinitionType,
  ExtractionType,
  ExtractorType,
  GuidedPreset,
  MatchLevel,
} from 'dashboard/models/document';
import {
  DocumentViewerGenericExtractionsActions,
  DocumentViewerGenericExtractionsActionType,
} from 'dashboard/redux/actions/document-viewer-document-generic';
import { Translations } from 'dashboard/redux/slices/translations';
import {
  ExtractionFilterOptions,
  getMatchLevelAndExtractionTypeForFilter,
} from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerExtractionFilter';
import { DocumentSearchMatch } from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerSearch';
import { getExtractionHighlightId } from 'dashboard/views/DashboardDocumentViewerView/ExtractionHighlights';
import { getElementIdForSearchHighlight } from 'dashboard/views/DashboardDocumentViewerView/SearchHighlights';

export interface ExtractionFilter {
  extractionType: ExtractionType;
  matchLevel: MatchLevel;
}

export enum ExtractionFilterValue {
  FullGuided = 'fullGuided',
  PartialGuided = 'partialGuided',
  Unguided = 'unguided',
}

export interface ExtractionGroup {
  // the following two fields are unique identifierse for an extraction group
  fieldName: string;
  fieldIdentifier?: string;
  extractionValue: string | null;
  // other fields are addition fields that are guaranteed to be the same
  // since they come from the same extraction and have the same extraction value
  extractionType: ExtractionType;
  extractorType?: ExtractorType;
  sourceValue?: string;
  matchLevelMinimum?: MatchLevel;
  extractionMatchLevel: MatchLevel | null;
  definitionType?: DefinitionType;
  guidedPreset?: GuidedPreset;
  numResults: number;
  filtered: boolean;
}

export interface ExtractionBoundingBox {
  page: number;
  boundingBox: BoundingBox;
  // unique id generated on the client side to differentiate ExtractionResponseResults within the
  // same extraction group
  id: number;
}

export interface DocumentViewerExtraction {
  extractionGroup: ExtractionGroup;
  results: ExtractionBoundingBox[];
}

export interface DocumentViewerSingletonExtraction {
  extractionGroup: ExtractionGroup;
  result: ExtractionBoundingBox;
}

export interface FocusedExtraction {
  extractionGroup: ExtractionGroup;
  focusedBoundingBoxId: number;
}

export interface DocumentViewerDocumentGenericState {
  extractions: DocumentViewerExtraction[];
  focusedExtractions: FocusedExtraction[];
  hoveredExtractions: FocusedExtraction[];
  extractionFilters: ExtractionFilterValue[];
  inSearchMode: boolean;
  searchMatches: DocumentSearchMatch[];
  searchIndex: number;
  numPagesPerFile: number[];
  translationLoading: boolean;
  translations: Translations | null;
  translationLanguage: GoogleSupportedLanguage | null;
}

const getInitialState: () => DocumentViewerDocumentGenericState = () => {
  return {
    extractions: [],
    focusedExtractions: [],
    hoveredExtractions: [],
    extractionFilters: ExtractionFilterOptions.map((filterOption) => filterOption.value),
    inSearchMode: false,
    searchMatches: [],
    searchIndex: -1,
    numPagesPerFile: [],
    translationLoading: false,
    translations: null,
    translationLanguage: null,
  };
};

export const initialState: DocumentViewerDocumentGenericState = getInitialState();

const documentViewerDocumentGenericReducer = (
  state: DocumentViewerDocumentGenericState = initialState,
  action: DocumentViewerGenericExtractionsActions,
): DocumentViewerDocumentGenericState => {
  switch (action.type) {
    case DocumentViewerGenericExtractionsActionType.ResetDocumentGenericState: {
      return getInitialState();
    }
    case DocumentViewerGenericExtractionsActionType.SetExtractions: {
      const documentViewerExtractions = action.payload.extractions.flatMap((extraction) => {
        const config = action.payload.extractionConfigs.find(
          (config) => config['field-name'] === extraction['field-name'],
        );
        const extractorType =
          config?.['extraction-type'] === ExtractionType.Guided
            ? ExtractorType.InquiryFieldComparison
            : config?.['extractor-type'];
        const definitionType =
          config?.['extraction-type'] === ExtractionType.Guided
            ? config['definition-type']
            : undefined;
        const guidedPreset =
          config?.['extraction-type'] === ExtractionType.Guided ? config.preset : undefined;

        if (extraction.results.length === 0) {
          const extractionGroup: ExtractionGroup = {
            fieldName: extraction['field-name'],
            extractionValue: null,
            extractionType: extraction['extraction-type'],
            extractorType,
            sourceValue: extraction['source-value'],
            matchLevelMinimum: extraction['match-level-minimum'],
            extractionMatchLevel: null,
            definitionType,
            guidedPreset,
            numResults: 0,
            filtered: false,
          };
          const results: ExtractionBoundingBox[] = [];

          return { extractionGroup, results };
        }

        const extractionResultsByValue = groupBy((result) => result.value, extraction.results);
        return Object.entries(extractionResultsByValue).flatMap(([value, valueResults]) => {
          const [withFieldIdentifiers, withoutFieldIdentifiers] = partition(
            (result) => result.metadata['field-identifier'],
            valueResults,
          );

          return [
            ...Object.entries(
              groupBy((result) => result.metadata['field-identifier'], withFieldIdentifiers),
            ),
            ...(withoutFieldIdentifiers.length > 0
              ? [[undefined, withoutFieldIdentifiers] as const]
              : []),
          ].map(([fieldIdentifier, fieldIdentifierResults]) => {
            const extractionGroup: ExtractionGroup = {
              fieldName: extraction['field-name'],
              fieldIdentifier,
              extractionValue: value,
              extractionType: extraction['extraction-type'],
              extractorType,
              sourceValue: extraction['source-value'],
              matchLevelMinimum: extraction['match-level-minimum'],
              extractionMatchLevel: fieldIdentifierResults[0]['match-level'],
              definitionType,
              guidedPreset,
              numResults: fieldIdentifierResults.length,
              filtered: false,
            };
            const results: ExtractionBoundingBox[] = fieldIdentifierResults
              .sort(
                (result, otherResult) =>
                  result.page - otherResult.page ||
                  result['bounding-box'].top - otherResult['bounding-box'].top ||
                  result['bounding-box'].left - otherResult['bounding-box'].left ||
                  result['bounding-box'].bottom - otherResult['bounding-box'].bottom ||
                  result['bounding-box'].right - otherResult['bounding-box'].right,
              )
              .map(({ page, 'bounding-box': boundingBox }, id) => ({ page, boundingBox, id }));

            return { extractionGroup, results };
          });
        });
      });
      return {
        ...state,
        extractions: documentViewerExtractions,
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetFocusedExtraction: {
      const focusedExtractions = action.payload.focusedExtractions;
      const selectedBB = document.getElementById(
        getExtractionHighlightId(
          focusedExtractions[0]?.extractionGroup,
          focusedExtractions[0]?.focusedBoundingBoxId,
        ),
      );
      selectedBB &&
        selectedBB.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      return {
        ...state,
        focusedExtractions: focusedExtractions,
      };
    }
    case DocumentViewerGenericExtractionsActionType.RemoveFocusedExtraction: {
      return {
        ...state,
        focusedExtractions: [],
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetHoveredExtraction: {
      return {
        ...state,
        hoveredExtractions: action.payload.hoveredExtractions,
      };
    }
    case DocumentViewerGenericExtractionsActionType.RemoveHoveredExtraction: {
      return {
        ...state,
        hoveredExtractions: [],
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetExtractionFilters: {
      const newExtractionFilters = action.payload.extractionFilters;
      const newExtractions = state.extractions.map((extraction) => {
        const filtered = !newExtractionFilters.some((extractionFilter) => {
          const { matchLevel, extractionType } =
            getMatchLevelAndExtractionTypeForFilter(extractionFilter);
          return (
            extraction.extractionGroup.extractionType === extractionType &&
            extraction.extractionGroup.extractionMatchLevel === matchLevel
          );
        });
        return {
          extractionGroup: {
            ...extraction.extractionGroup,
            filtered: filtered,
          },
          results: extraction.results,
        };
      });
      return {
        ...state,
        extractionFilters: newExtractionFilters,
        extractions: newExtractions,
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetInSearchMode: {
      return {
        ...state,
        inSearchMode: action.payload.inSearchMode,
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetSearchHighlights: {
      return {
        ...state,
        searchMatches: action.payload.searchMatches,
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetSearchIndex: {
      const focusedHighlightId = getElementIdForSearchHighlight(action.payload.searchIndex);
      const focusedSearchHighlight = document.getElementById(focusedHighlightId);
      focusedSearchHighlight &&
        // scrollIntoView doesn't work with behavior smooth unless there is a delay.
        // https://stackoverflow.com/questions/71181018/scrollintoview-doesnt-scroll-anywhere
        // alternatively, scrollIntoView with behavior auto works without the delay, but i thought this was a better UX.
        setTimeout(
          () =>
            focusedSearchHighlight.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            }),
          100,
        );
      return {
        ...state,
        searchIndex: action.payload.searchIndex,
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetFileNumPages: {
      const newNumPagesPerFile = [...state.numPagesPerFile];
      newNumPagesPerFile[action.payload.fileIndex] = action.payload.numPages;
      return {
        ...state,
        numPagesPerFile: newNumPagesPerFile,
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetTranslationLoading: {
      return {
        ...state,
        translationLoading: action.payload.translationLoading,
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetTranslations: {
      return {
        ...state,
        translations: action.payload.translations,
      };
    }
    case DocumentViewerGenericExtractionsActionType.SetTranslationLanguage: {
      return {
        ...state,
        translationLanguage: action.payload.translationLanguage,
      };
    }
    default:
      return state;
  }
};

export default documentViewerDocumentGenericReducer;
